import { NotFound } from "@vana/core";
import { createBrowserRouter } from "react-router";
import { RequireAuth, RequireRole } from "../../auth/guards";
import { AmbassadorShell } from "../ambassador-shell/ambassador-shell";
import { BackofficeShell } from "../backoffice-shell/backoffice-shell";
import { Login } from "../login/login";
import { Pending } from "../pending/pending";
import { RoleHome } from "../role-home/role-home";
import { AppRoutes, routes } from "./routes";

export const router = createBrowserRouter([
  { path: AppRoutes.LOGIN, element: <Login /> },

  // Everything below requires an authenticated corporate session.
  {
    element: <RequireAuth />,
    children: [
      // Root dispatches by role; keeping it out of the embajador area is what
      // stops a role mismatch from redirect-looping.
      { index: true, element: <RoleHome /> },

      // Self-registered users without a role yet wait here.
      { path: AppRoutes.PENDING, element: <Pending /> },

      // Embajador area — its own role gate + mobile shell. Screens are lazy so
      // this bundle stays separate from the (future) backoffice area.
      {
        element: <RequireRole role="ambassador" />,
        children: [
          {
            element: <AmbassadorShell />,
            handle: { routes },
            children: [
              {
                path: AppRoutes.AMBASSADOR_ROUTE,
                lazy: async () => {
                  const { RouteScreen } = await import("../../routes/route-screen");
                  return { Component: RouteScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_DAY_CLOSE,
                lazy: async () => {
                  const { DayCloseScreen } = await import("../../routes/day-close-screen");
                  return { Component: DayCloseScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_DELIVERY,
                lazy: async () => {
                  const { DeliveryDetailScreen } = await import("../../routes/delivery-detail-screen");
                  return { Component: DeliveryDetailScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_KYC,
                lazy: async () => {
                  const { KycScreen } = await import("../../routes/kyc-screen");
                  return { Component: KycScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_POD,
                lazy: async () => {
                  const { RegisterDeliveryScreen } = await import("../../routes/register-delivery-screen");
                  return { Component: RegisterDeliveryScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_EDUCATION,
                lazy: async () => {
                  const { FinancialEducationScreen } = await import("../../routes/financial-education-screen");
                  return { Component: FinancialEducationScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_FIRST_PURCHASE,
                lazy: async () => {
                  const { FirstPurchaseScreen } = await import("../../routes/first-purchase-screen");
                  return { Component: FirstPurchaseScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_REFERRALS,
                lazy: async () => {
                  const { ReferralsScreen } = await import("../../routes/referrals-screen");
                  return { Component: ReferralsScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_FAILED_DELIVERY,
                lazy: async () => {
                  const { FailedDeliveryScreen } = await import("../../routes/failed-delivery-screen");
                  return { Component: FailedDeliveryScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_PROBLEM_REPORT,
                lazy: async () => {
                  const { ProblemReportScreen } = await import("../../routes/problem-report-screen");
                  return { Component: ProblemReportScreen };
                },
              },
              {
                path: AppRoutes.AMBASSADOR_CAPTURE,
                lazy: async () => {
                  const { PhotoCaptureScreen } = await import("../../routes/photo-capture-screen");
                  return { Component: PhotoCaptureScreen };
                },
              },
            ],
          },
        ],
      },

      // Contact Center + Supervisor shared area — the "Por confirmar" queue
      // (VCE-664). Its gate admits either role; the path lives only here so both
      // roles resolve it (react-router matches the first path occurrence).
      {
        element: <RequireRole role={["supervisor", "contact_center"]} />,
        children: [
          {
            element: <BackofficeShell />,
            handle: { routes },
            children: [
              {
                path: AppRoutes.PENDING_CONFIRMATION,
                lazy: async () => {
                  const { PendingConfirmationScreen } = await import(
                    "../../routes/pending-confirmation-screen"
                  );
                  return { Component: PendingConfirmationScreen };
                },
              },
            ],
          },
        ],
      },

      // "No entregadas" (DMS-35) — the four backoffice roles that work the
      // section. The gate admits all of them and the path lives only here, so
      // every role resolves it. "Reabrir" is hidden from the Contact Center
      // inside the screen, and refused by the API for good measure.
      {
        element: (
          <RequireRole
            role={["supervisor", "contact_center", "operations", "admin"]}
          />
        ),
        children: [
          {
            element: <BackofficeShell />,
            handle: { routes },
            children: [
              {
                path: AppRoutes.UNDELIVERED,
                lazy: async () => {
                  const { UndeliveredScreen } = await import(
                    "../../routes/undelivered-screen"
                  );
                  return { Component: UndeliveredScreen };
                },
              },
            ],
          },
        ],
      },

      // Supervisor + Admin shared area — "Todas las entregas" (the list) and the
      // delivery detail it links to. One gate admits either role; the paths live
      // only here so both roles resolve them (react-router matches the first
      // occurrence; the static list path outranks the dynamic detail path).
      {
        element: <RequireRole role={["supervisor", "admin"]} />,
        children: [
          {
            element: <BackofficeShell />,
            handle: { routes },
            children: [
              {
                path: AppRoutes.ALL_DELIVERIES,
                lazy: async () => {
                  const { AllDeliveriesScreen } = await import("../../routes/all-deliveries-screen");
                  return { Component: AllDeliveriesScreen };
                },
              },
              {
                path: AppRoutes.SUPERVISOR_DELIVERY,
                lazy: async () => {
                  const { SupervisorDeliveryScreen } = await import(
                    "../../routes/supervisor-delivery-screen"
                  );
                  return { Component: SupervisorDeliveryScreen };
                },
              },
              {
                path: AppRoutes.REFERRALS_BACKOFFICE,
                lazy: async () => {
                  const { ReferralsBackofficeScreen } = await import(
                    "../../routes/referrals-backoffice-screen"
                  );
                  return { Component: ReferralsBackofficeScreen };
                },
              },
            ],
          },
        ],
      },

      // Compliance area (DMS-27) — read-only "Clientes" + "Caso del cliente",
      // shared by the compliance role, admin and operations. One gate admits the
      // three roles; the paths live only here.
      {
        element: <RequireRole role={["compliance", "admin", "operations"]} />,
        children: [
          {
            element: <BackofficeShell />,
            handle: { routes },
            children: [
              {
                path: AppRoutes.COMPLIANCE_CUSTOMERS,
                lazy: async () => {
                  const { ComplianceCustomersScreen } = await import(
                    "../../routes/compliance-customers-screen"
                  );
                  return { Component: ComplianceCustomersScreen };
                },
              },
              {
                path: AppRoutes.COMPLIANCE_CASE,
                lazy: async () => {
                  const { ComplianceCaseScreen } = await import("../../routes/compliance-case-screen");
                  return { Component: ComplianceCaseScreen };
                },
              },
            ],
          },
        ],
      },

      // Backoffice area (supervisor) — its own role gate + desktop shell. Its
      // screens are lazy so this bundle stays separate from the embajador area.
      {
        element: <RequireRole role="supervisor" />,
        children: [
          {
            element: <BackofficeShell />,
            handle: { routes },
            children: [
              {
                path: AppRoutes.SUPERVISOR_SUMMARY,
                lazy: async () => {
                  const { SummaryScreen } = await import("../../routes/summary-screen");
                  return { Component: SummaryScreen };
                },
              },
              {
                path: AppRoutes.SUPERVISOR_ROUTE_ASSIGNMENT,
                lazy: async () => {
                  const { RouteAssignmentScreen } = await import(
                    "../../routes/route-assignment-screen"
                  );
                  return { Component: RouteAssignmentScreen };
                },
              },
              {
                path: AppRoutes.SUPERVISOR_REPORTS,
                lazy: async () => {
                  const { ReportsScreen } = await import("../../routes/reports-screen");
                  return { Component: ReportsScreen };
                },
              },
              {
                path: AppRoutes.SUPERVISOR_RESCHEDULES,
                lazy: async () => {
                  const { ReschedulesScreen } = await import("../../routes/reschedules-screen");
                  return { Component: ReschedulesScreen };
                },
              },
              {
                path: AppRoutes.SUPERVISOR_TEAM,
                lazy: async () => {
                  const { TeamScreen } = await import("../../routes/team-screen");
                  return { Component: TeamScreen };
                },
              },
              {
                path: AppRoutes.SUPERVISOR_AMBASSADOR_DELIVERIES,
                lazy: async () => {
                  const { AmbassadorDeliveriesScreen } = await import(
                    "../../routes/ambassador-deliveries-screen"
                  );
                  return { Component: AmbassadorDeliveriesScreen };
                },
              },
            ],
          },
        ],
      },

      // Backoffice area (admin) — its own role gate reusing the desktop shell.
      // Admin cannot reach the supervisor gate (RequireRole rejects mismatches),
      // so it needs a sibling gate; the shell + backoffice chunk are shared.
      {
        element: <RequireRole role="admin" />,
        children: [
          {
            element: <BackofficeShell />,
            handle: { routes },
            children: [
              {
                path: AppRoutes.ADMIN_USERS,
                lazy: async () => {
                  const { UsersScreen } = await import("../../routes/users-screen");
                  return { Component: UsersScreen };
                },
              },
              {
                path: AppRoutes.ADMIN_ZONES,
                lazy: async () => {
                  const { ZonesScreen } = await import("../../routes/zones-screen");
                  return { Component: ZonesScreen };
                },
              },
            ],
          },
        ],
      },
      // Operations gate — its own role gate; the shell + backoffice chunk are
      // shared with supervisor/admin.
      {
        element: <RequireRole role="operations" />,
        children: [
          {
            element: <BackofficeShell />,
            handle: { routes },
            children: [
              {
                path: AppRoutes.OPERATIONS_BATCHES,
                lazy: async () => {
                  const { BatchesScreen } = await import("../../routes/batches-screen");
                  return { Component: BatchesScreen };
                },
              },
              {
                path: AppRoutes.OPERATIONS_INVENTORY,
                lazy: async () => {
                  const { InventoryScreen } = await import("../../routes/inventory-screen");
                  return { Component: InventoryScreen };
                },
              },
              {
                path: AppRoutes.OPERATIONS_DISTRIBUTION,
                lazy: async () => {
                  const { DistributionScreen } = await import("../../routes/distribution-screen");
                  return { Component: DistributionScreen };
                },
              },
              {
                path: AppRoutes.OPERATIONS_DAY_CLOSE,
                lazy: async () => {
                  const { DayReceptionScreen } = await import("../../routes/day-reception-screen");
                  return { Component: DayReceptionScreen };
                },
              },
              {
                path: AppRoutes.OPERATIONS_CUSTODY,
                lazy: async () => {
                  const { CustodyScreen } = await import("../../routes/custody-screen");
                  return { Component: CustodyScreen };
                },
              },
            ],
          },
        ],
      },
    ],
  },

  { path: "*", element: <NotFound />, handle: { routes } },
]);
